/**
 * Entity URL utilities — single source of truth for generating navigation URLs
 * from entity types/refs.
 *
 * The canonical entity → URL map is generated from `shared/config/modules_entities.config.json`
 * into `apps/frontend/src/generated/entity-urls.ts`. The matching Python registry is at
 * `shared/generated/entity_urls.py`. Both are regenerated by the pre-commit hook when
 * the config changes — never edit the generated files by hand.
 *
 * Two entry points:
 * - getEntityUrl(): for resolved entities (entity_type + entity_id + optional context)
 * - getEntityUrlFromRef(): for raw entity refs ("table_name:uuid") from MCP tool calls
 *
 * Both look up `entityType` in the generated registry. The registry supports
 * a `urlContextTemplate` (used when extra context like board_id is available);
 * the resolver prefers it when fillable, else falls back to `urlTemplate` —
 * unless the caller passes `contextTemplateOnly`, which makes an unfillable
 * contextual template resolve to null instead.
 *
 * The registry is the ONLY source of a URL. A key it does not carry resolves to
 * null — a non-clickable chip, an absent link — never to a guess. There used to
 * be a "convention" fallback here that derived `/{module}/{entity}/{id}` from the
 * key's spelling, so that a chip stayed clickable before its entity was in the
 * config. It could not tell a real route from an invented one: an AI-chat tool
 * ref `pipeline_leads:{id}` (a lead IS a contact, whose page is
 * `/master-data/contacts/{id}`) became `/pipeline/leads/{id}`, a route that has
 * never existed, and every chip the assistant created linked to a 404 (WI
 * 8b473eb7). Declare the key in the entity's `urlAliases` instead; the
 * integrity test beside this file checks every alias reaches a real route.
 */
import {
  ENTITY_URL_REGISTRY,
  fillTemplate,
  resolveEntityUrl,
} from '../../generated/entity-urls';

// ---------- types ----------

export interface EntityUrlOptions {
  entityType: string;
  entityId: string;
  boardId?: string;
  linkToFullPage?: boolean;
  /** Extra context for nested entities */
  context?: Record<string, string>;
  /**
   * When the entry declares a `urlContextTemplate` and the caller has NOT asked
   * for the full page, resolve ONLY through that contextual template — return
   * null instead of falling back to `urlTemplate`.
   *
   * For a chip whose whole job is "open this record where the user was looking
   * at it": a task chip that isn't asking for the full page belongs in its board
   * drawer, and a task carrying no `board_id` has no such destination. Falling
   * back to `/tasks/tasks/{id}` there would silently change what the chip means.
   * `EntityIconChip` renders a non-interactive span in that case, which is also
   * what lets it sit inside a button.
   */
  contextTemplateOnly?: boolean;
}

// ---------- public API ----------

/**
 * Generate a URL path for navigating to a specific entity.
 * Used by EntityChip and anywhere we have a resolved entity_type + entity_id.
 */
export function getEntityUrl(options: EntityUrlOptions): string | null {
  const { entityType, entityId, boardId, linkToFullPage, context, contextTemplateOnly } = options;

  const entry = ENTITY_URL_REGISTRY[entityType];
  if (!entry) return null;

  const values: Record<string, string | undefined> = {
    id: entityId,
    ...(context ?? {}),
    ...(boardId ? { board_id: boardId } : {}),
  };

  // Prefer the contextual template when its placeholders can be filled and
  // the caller hasn't explicitly asked for the full-page URL.
  if (entry.urlContextTemplate && !linkToFullPage) {
    const ctxUrl = fillTemplate(entry.urlContextTemplate, values);
    if (ctxUrl) return ctxUrl;
    if (contextTemplateOnly) return null;
  }

  // A context-only entry (no urlTemplate — a child edited on a tab of its
  // parent's page) has nothing to fall back to. Null, never a half-filled path.
  return entry.urlTemplate ? fillTemplate(entry.urlTemplate, values) : null;
}

/**
 * Narrow a loose payload (a notification's `data`) to usable URL-template context.
 *
 * A nested entity's `urlContextTemplate` needs more than `{id}` — a chatbot
 * channel needs `bot_id`, an agent approval needs `agent_id` — and emit sites
 * already carry those. Only string values are eligible, and `id` is dropped:
 * `getEntityUrl` spreads context OVER `{id}`, so a payload that happened to carry
 * its own `id` would silently retarget the link away from the actual entity.
 *
 * Mirrors `NotificationEmailService._url_context` (shared/notifications/email_delivery.py)
 * so the email CTA and the in-app surfaces resolve the same destination.
 */
export function entityUrlContext(
  data: Record<string, unknown> | null | undefined,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(data ?? {}).filter(
      (entry): entry is [string, string] => entry[0] !== 'id' && typeof entry[1] === 'string',
    ),
  );
}

/**
 * Resolve the list-page URL for an entity type from the URL registry.
 *
 * The registry stores per-entity *detail* templates (e.g.
 * `/master-data/organizations/{id}`). A list page is that template with the
 * trailing `/{id}` segment removed. Returns null when the entity type has no
 * registry entry, declares no canonical `urlTemplate` at all (a child entity
 * edited on a tab of its parent's page — the parent owns the list), or its
 * detail template isn't a plain `/path/{id}` shape (e.g. query-param routes
 * like `/ai-chat?session={id}`).
 *
 * Used by tag chips to navigate to a tag-filtered list page.
 */
export function getEntityListUrl(entityType: string): string | null {
  const entry = ENTITY_URL_REGISTRY[entityType];
  if (!entry) return null;
  const template = entry.urlTemplate;
  if (!template || !template.endsWith('/{id}')) return null;
  return template.slice(0, -'/{id}'.length);
}

/**
 * Resolve the URL of a list page filtered by one or more tags.
 * Returns null when the entity type has no derivable list URL.
 */
export function getTagFilteredListUrl(
  entityType: string,
  tagIds: string[],
): string | null {
  const listUrl = getEntityListUrl(entityType);
  if (!listUrl || tagIds.length === 0) return null;
  return `${listUrl}?tags=${tagIds.map(encodeURIComponent).join(',')}`;
}

/**
 * Generate a URL from a raw entity ref string ("table_name:uuid").
 * Used by tool call entity chips in chat.
 */
export function getEntityUrlFromRef(ref: string, context?: Record<string, string>): string | null {
  const colonIdx = ref.indexOf(':');
  if (colonIdx === -1) return null;

  const entityType = ref.substring(0, colonIdx);
  const entityId = ref.substring(colonIdx + 1);
  if (!entityType || !entityId) return null;

  return getEntityUrl({ entityType, entityId, context });
}

/**
 * Navigate to an entity's detail page using the URL registry.
 *
 * Preferred over `navigate(\`/sales/deals/${id}\`)` — keeps entity URL shapes
 * in one place (`shared/config/modules_entities.config.json`) and stays in
 * sync if a route ever moves.
 *
 * Usage:
 *   navigateToEntity(navigate, 'sales_deals', deal.id);
 *   navigateToEntity(navigate, 'sales_deals', deal.id, { replace: true });
 *   navigateToEntity(navigate, 'tasks_tasks', task.id, { context: { board_id } });
 */
export function navigateToEntity(
  navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void,
  entityType: string,
  entityId: string,
  options?: {
    replace?: boolean;
    state?: unknown;
    context?: Record<string, string>;
    linkToFullPage?: boolean;
  },
): void {
  const url = getEntityUrl({
    entityType,
    entityId,
    context: options?.context,
    linkToFullPage: options?.linkToFullPage,
  });
  if (url) {
    navigate(url, { replace: options?.replace, state: options?.state });
  }
}

/**
 * Re-export the generated resolver for callers that work directly with arbitrary
 * key/value contexts.
 *
 * **Prefer `getEntityUrl` above.** `resolveEntityUrl` re-implements the same
 * "prefer `urlContextTemplate`, else fall back to `urlTemplate`" preference and
 * knows nothing about `contextTemplateOnly` — so a caller that
 * needs "the contextual URL or no link at all" gets the full-page URL from here,
 * silently. Two resolvers with nobody comparing them is the shape that let
 * `EntityIconChip`'s hand-rolled `users` destination go stale in the first place.
 */
export { resolveEntityUrl, ENTITY_URL_REGISTRY };
